import React, { useState, useEffect } from 'react';
import AboutPage from './AboutPage';
import ContactPage from './ContactPage';
import PortfolioPage from './PortfolioPage';
import Preloader from './Preloader';

// Import App lazily to avoid any circular-resolution issues
const BankApp = React.lazy(() => import('./App'));

type PublicRoute = 'app' | 'about' | 'contact' | 'portfolio';

// Bridge so App.tsx landing buttons can call window.__pubNav('about') etc.
declare global {
  interface Window { __pubNav?: (page: string) => void; }
}

function getInitialRoute(): PublicRoute {
  const hash = window.location.hash;
  if (hash === '#about')     return 'about';
  if (hash === '#contact')   return 'contact';
  if (hash === '#portfolio') return 'portfolio';
  return 'app';
}

export default function PublicSite() {
  const [route, setRoute] = useState<PublicRoute>(getInitialRoute);
  const [ready, setReady]  = useState(false);

  const navigate = (page: string) => {
    setRoute((page === 'landing' ? 'app' : page) as PublicRoute);
  };

  // Register globally so App.tsx goPublic() can reach this
  useEffect(() => {
    window.__pubNav = navigate;
    return () => { delete window.__pubNav; };
  });

  // Keep URL hash in sync
  useEffect(() => {
    window.location.hash = route === 'app' ? '' : route;
  }, [route]);

  // Browser back / forward
  useEffect(() => {
    const handler = () => setRoute(getInitialRoute());
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);

  if (!ready) {
    return <Preloader duration={2600} onDone={() => setReady(true)} />;
  }

  if (route === 'about')     return <AboutPage     onNavigate={navigate} />;
  if (route === 'contact')   return <ContactPage   onNavigate={navigate} />;
  if (route === 'portfolio') return <PortfolioPage onNavigate={navigate} />;

  // Render the banking app — React.lazy handles the import cleanly
  return (
    <React.Suspense fallback={null}>
      <BankApp />
    </React.Suspense>
  );
}
